'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function CaloriesBurnedCalculator() {
  const t = useTranslations("tools.caloriesBurnedCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    weight: 150,
    duration: 30,
    unit: 'imperial',
    category: '',
    selectedActivity: null as any
  });

  const [results, setResults] = useState<any>(null);
  const [showActivityList, setShowActivityList] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Activities database with MET values
  const activities = {
    cardio: [
      { name: t('activities.cardio.walkingSlow'), met: 2.0 },
      { name: t('activities.cardio.walkingModerate'), met: 3.5 },
      { name: t('activities.cardio.walkingFast'), met: 5.0 },
      { name: t('activities.cardio.walkingVeryFast'), met: 6.3 },
      { name: t('activities.cardio.hiking'), met: 6.0 },
      { name: t('activities.cardio.runningSlow'), met: 8.0 },
      { name: t('activities.cardio.runningModerate'), met: 9.8 },
      { name: t('activities.cardio.runningFast'), met: 11.0 },
      { name: t('activities.cardio.runningVeryFast'), met: 11.8 },
      { name: t('activities.cardio.runningCrossCountry'), met: 9.0 },
      { name: t('activities.cardio.cyclingSlow'), met: 4.0 },
      { name: t('activities.cardio.cyclingModerate'), met: 8.0 },
      { name: t('activities.cardio.cyclingFast'), met: 10.0 },
      { name: t('activities.cardio.cyclingVeryFast'), met: 12.0 },
      { name: t('activities.cardio.cyclingBMX'), met: 8.5 },
      { name: t('activities.cardio.swimmingModerate'), met: 5.8 },
      { name: t('activities.cardio.swimmingVigorous'), met: 9.8 }
    ],
    gym: [
      { name: t('activities.gym.aerobicsStepHigh'), met: 8.5 },
      { name: t('activities.gym.aerobicsStepLow'), met: 5.5 },
      { name: t('activities.gym.aerobicsHigh'), met: 7.0 },
      { name: t('activities.gym.aerobicsLow'), met: 5.0 },
      { name: t('activities.gym.aerobicsWater'), met: 5.3 },
      { name: t('activities.gym.cyclingStationaryModerate'), met: 6.8 },
      { name: t('activities.gym.cyclingStationaryVigorous'), met: 10.5 },
      { name: t('activities.gym.calisthenicsModerate'), met: 3.8 },
      { name: t('activities.gym.calisthenicsVigorous'), met: 8.0 },
      { name: t('activities.gym.circuitTraining'), met: 8.0 },
      { name: t('activities.gym.elliptical'), met: 5.0 },
      { name: t('activities.gym.rowingModerate'), met: 7.0 },
      { name: t('activities.gym.rowingVigorous'), met: 8.5 },
      { name: t('activities.gym.skiMachine'), met: 7.0 },
      { name: t('activities.gym.stairMachine'), met: 9.0 },
      { name: t('activities.gym.yoga'), met: 2.5 },
      { name: t('activities.gym.weightLiftingGeneral'), met: 3.0 },
      { name: t('activities.gym.weightLiftingVigorous'), met: 6.0 }
    ],
    sports: [
      { name: t('activities.sports.badminton'), met: 5.5 },
      { name: t('activities.sports.basketballGame'), met: 8.0 },
      { name: t('activities.sports.basketballWheelchair'), met: 6.5 },
      { name: t('activities.sports.bowling'), met: 3.0 },
      { name: t('activities.sports.dancingDisco'), met: 5.5 },
      { name: t('activities.sports.dancingSlow'), met: 3.0 },
      { name: t('activities.sports.football'), met: 9.0 },
      { name: t('activities.sports.frisbee'), met: 3.0 },
      { name: t('activities.sports.golfCart'), met: 3.5 },
      { name: t('activities.sports.golfCarrying'), met: 4.3 },
      { name: t('activities.sports.handball'), met: 12.0 },
      { name: t('activities.sports.racquetball'), met: 7.0 },
      { name: t('activities.sports.rockClimbing'), met: 11.0 },
      { name: t('activities.sports.rollerblading'), met: 7.0 },
      { name: t('activities.sports.ropeJumping'), met: 12.0 },
      { name: t('activities.sports.rugby'), met: 10.0 },
      { name: t('activities.sports.soccer'), met: 7.0 },
      { name: t('activities.sports.tennis'), met: 8.0 },
      { name: t('activities.sports.volleyball'), met: 6.0 }
    ],
    outdoor: [
      { name: t('activities.outdoor.horsebackRiding'), met: 5.5 },
      { name: t('activities.outdoor.kayaking'), met: 5.0 },
      { name: t('activities.outdoor.skiingDownhill'), met: 5.3 },
      { name: t('activities.outdoor.skiingCrossCountry'), met: 9.0 },
      { name: t('activities.outdoor.snowboarding'), met: 5.3 },
      { name: t('activities.outdoor.surfing'), met: 3.0 },
      { name: t('activities.outdoor.skateboarding'), met: 5.0 }
    ],
    daily: [
      { name: t('activities.daily.cleaning'), met: 3.5 },
      { name: t('activities.daily.cooking'), met: 2.5 },
      { name: t('activities.daily.gardening'), met: 4.0 },
      { name: t('activities.daily.mowingLawn'), met: 5.5 },
      { name: t('activities.daily.playingWithKids'), met: 4.0 },
      { name: t('activities.daily.shopping'), met: 3.5 },
      { name: t('activities.daily.sittingOffice'), met: 1.5 },
      { name: t('activities.daily.standingLight'), met: 2.0 },
      { name: t('activities.daily.vacuuming'), met: 3.5 }
    ]
  };

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)', overflow: 'hidden' },
      { maxWidth: '100%' },
      { maxWidth: '700px' },
      { maxWidth: '900px' }
    ),
    toolHeader: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      padding: '2rem',
      textAlign: 'center' as const,
    },
    toolHeaderTitle: getResponsiveStyle(
      { margin: 0, fontWeight: 700, color: 'white' },
      { fontSize: '1.5rem' },
      { fontSize: '1.6rem' },
      { fontSize: '1.8rem' }
    ),
    calculatorForm: getResponsiveStyle(
      {},
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    unitToggle: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '1.5rem',
      background: '#f8fafc',
      padding: '0.5rem',
      borderRadius: '0.5rem',
    },
    unitToggleLabel: {
      flex: 1,
      textAlign: 'center' as const,
      padding: '0.75rem',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      fontWeight: 600,
      transition: 'all 0.3s ease',
      background: 'white',
      border: '2px solid #e2e8f0',
    },
    unitToggleLabelActive: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      borderColor: '#2563eb',
    },
    formRow: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      fontWeight: 600,
      color: '#1e293b',
      fontSize: '0.95rem',
    },
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
      },
      { padding: '0.75rem' },
      { padding: '0.875rem 1rem' },
      { padding: '0.875rem 1rem' }
    ),
    select: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        boxSizing: 'border-box' as const,
        background: 'white',
      },
      { padding: '0.75rem' },
      { padding: '0.875rem 1rem' },
      { padding: '0.875rem 1rem' }
    ),
    activityGrid: getResponsiveStyle(
      {
        display: 'grid',
        gap: '0.75rem',
        marginTop: '1rem',
        maxHeight: '300px',
        overflowY: 'auto' as const,
        padding: '1rem',
        background: '#f8fafc',
        borderRadius: '0.5rem',
      },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    activityOption: {
      display: 'flex',
      alignItems: 'center',
      padding: '0.5rem',
      cursor: 'pointer',
      borderRadius: '0.375rem',
      transition: 'background 0.2s ease',
    },
    activityOptionLabel: {
      cursor: 'pointer',
      fontSize: '0.9rem',
      margin: 0,
      fontWeight: 500,
      marginLeft: '0.5rem',
    },
    calculateButton: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '1rem',
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.125rem' },
      { padding: '1rem 2rem', fontSize: '1.1rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        borderTop: '3px solid #22c55e',
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { margin: '0 0 1.5rem 0', color: '#15803d', textAlign: 'center' as const },
      { fontSize: '1.3rem' },
      { fontSize: '1.4rem' },
      { fontSize: '1.5rem' }
    ),
    resultMain: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '0.75rem',
        textAlign: 'center' as const,
        marginBottom: '1.5rem',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    resultMainValue: getResponsiveStyle(
      {
        fontWeight: 800,
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        margin: '0.5rem 0',
      },
      { fontSize: '2.5rem' },
      { fontSize: '2.75rem' },
      { fontSize: '3rem' }
    ),
    resultMainLabel: getResponsiveStyle(
      { color: '#64748b', fontWeight: 600 },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.1rem' }
    ),
    resultDetails: getResponsiveStyle(
      { display: 'grid', gap: '1rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    resultItem: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '0.5rem',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
      },
      { padding: '1rem' },
      { padding: '1.125rem' },
      { padding: '1.25rem' }
    ),
    resultLabel: {
      display: 'block',
      fontSize: '0.85rem',
      color: '#64748b',
      fontWeight: 600,
      marginBottom: '0.5rem',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
    },
    resultValue: getResponsiveStyle(
      { display: 'block', fontWeight: 700, color: '#1e293b' },
      { fontSize: '1.3rem' },
      { fontSize: '1.4rem' },
      { fontSize: '1.5rem' }
    ),
    infoBox: {
      background: '#eff6ff',
      borderLeft: '4px solid #2563eb',
      padding: '1rem',
      marginTop: '1.5rem',
      borderRadius: '0.5rem',
    },
    infoBoxText: {
      margin: 0,
      color: '#1e40af',
      fontSize: '0.9rem',
      lineHeight: 1.6,
    },
    errorMessage: {
      background: '#fef2f2',
      borderLeft: '4px solid #ef4444',
      padding: '1rem',
      marginTop: '1rem',
      borderRadius: '0.5rem',
      color: '#991b1b',
    },
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    h3: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem',
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.1875rem' },
      { fontSize: '1.25rem' }
    ),
    reference: {
      marginBottom: '0.75rem',
    },
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      fontWeight: 500,
      transition: 'color 0.3s ease',
    },
  };

  const handleCategoryChange = (category: string) => {
    setInputs({ ...inputs, category, selectedActivity: null });
    setShowActivityList(!!category);
  };

  const handleActivitySelect = (activity: any) => {
    setInputs({ ...inputs, selectedActivity: activity });
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!inputs.selectedActivity) {
      setErrorMessage(t('errorSelectActivity'));
      return;
    }

    let weightKg = inputs.weight;
    if (inputs.unit === 'imperial') {
      weightKg = inputs.weight * 0.453592;
    }

    const caloriesPerMinute = (inputs.selectedActivity.met * 3.5 * weightKg) / 200;
    const totalCalories = caloriesPerMinute * inputs.duration;

    let intensity = t('intensityLight');
    if (inputs.selectedActivity.met >= 6) intensity = t('intensityVigorous');
    else if (inputs.selectedActivity.met >= 3) intensity = t('intensityModerate');

    const walkingEquivalent = Math.round(totalCalories / 4);

    setResults({
      totalCalories: Math.round(totalCalories),
      caloriesPerMinute: caloriesPerMinute.toFixed(1),
      metValue: inputs.selectedActivity.met.toFixed(1),
      intensity,
      equivalent: `${walkingEquivalent} ${t('minWalking')}`,
    });
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.toolHeaderTitle}>🔥 {t('toolTitle')}</h2>
          </div>

          <form style={styles.calculatorForm} onSubmit={handleCalculate}>
            <div style={styles.unitToggle}>
              <input
                type="radio"
                name="unit"
                id="unit-imperial"
                value="imperial"
                checked={inputs.unit === 'imperial'}
                onChange={() => setInputs({ ...inputs, unit: 'imperial' })}
                style={{ display: 'none' }}
              />
              <label
                htmlFor="unit-imperial"
                style={{
                  ...styles.unitToggleLabel,
                  ...(inputs.unit === 'imperial' ? styles.unitToggleLabelActive : {}),
                }}
              >
                {t('unitImperial')}
              </label>
              <input
                type="radio"
                name="unit"
                id="unit-metric"
                value="metric"
                checked={inputs.unit === 'metric'}
                onChange={() => setInputs({ ...inputs, unit: 'metric' })}
                style={{ display: 'none' }}
              />
              <label
                htmlFor="unit-metric"
                style={{
                  ...styles.unitToggleLabel,
                  ...(inputs.unit === 'metric' ? styles.unitToggleLabelActive : {}),
                }}
              >
                {t('unitMetric')}
              </label>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label htmlFor="weight" style={styles.label}>
                  {t('labelWeight')}
                </label>
                <input
                  type="number"
                  id="weight"
                  value={inputs.weight}
                  onChange={(e) => setInputs({ ...inputs, weight: parseFloat(e.target.value) || 0 })}
                  placeholder={t('placeholderWeight')}
                  required
                  min="1"
                  step="0.1"
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="duration" style={styles.label}>
                  {t('labelDuration')}
                </label>
                <input
                  type="number"
                  id="duration"
                  value={inputs.duration}
                  onChange={(e) => setInputs({ ...inputs, duration: parseFloat(e.target.value) || 0 })}
                  placeholder={t('placeholderDuration')}
                  required
                  min="1"
                  step="1"
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label htmlFor="category" style={styles.label}>
                {t('labelCategory')}
              </label>
              <select
                id="category"
                value={inputs.category}
                onChange={(e) => handleCategoryChange(e.target.value)}
                required
                style={styles.select}
              >
                <option value="">{t('selectCategory')}</option>
                <option value="cardio">{t('categoryCardio')}</option>
                <option value="gym">{t('categoryGym')}</option>
                <option value="sports">{t('categorySports')}</option>
                <option value="outdoor">{t('categoryOutdoor')}</option>
                <option value="daily">{t('categoryDaily')}</option>
              </select>
            </div>

            {showActivityList && (
              <div style={styles.formGroup}>
                <label style={styles.label}>{t('labelSelectActivity')}</label>
                <div style={styles.activityGrid}>
                  {activities[inputs.category as keyof typeof activities]?.map((activity, index) => (
                    <div
                      key={index}
                      style={{
                        ...styles.activityOption,
                        ...(inputs.selectedActivity?.name === activity.name
                          ? { background: '#e0f2fe' }
                          : {}),
                      }}
                      onMouseEnter={(e) => {
                        if (inputs.selectedActivity?.name !== activity.name) {
                          e.currentTarget.style.background = '#e0f2fe';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (inputs.selectedActivity?.name !== activity.name) {
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                    >
                      <input
                        type="radio"
                        name="activity"
                        id={`activity-${index}`}
                        checked={inputs.selectedActivity?.name === activity.name}
                        onChange={() => handleActivitySelect(activity)}
                        style={{ cursor: 'pointer' }}
                      />
                      <label htmlFor={`activity-${index}`} style={styles.activityOptionLabel}>
                        {activity.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {errorMessage && (
              <div style={styles.errorMessage}>
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              style={styles.calculateButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 25px rgba(245, 158, 11, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={styles.results}>
              <h3 style={styles.resultsTitle}>📊 {t('resultsTitle')}</h3>

              <div style={styles.resultMain}>
                <div style={styles.resultMainLabel}>{t('resultTotalCalories')}</div>
                <div style={styles.resultMainValue}>{results.totalCalories}</div>
                <div style={styles.resultMainLabel}>{t('kcal')}</div>
              </div>

              <div style={styles.resultDetails}>
                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t('resultCalPerMin')}</span>
                  <span style={styles.resultValue}>{results.caloriesPerMinute}</span>
                </div>

                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t('resultMetValue')}</span>
                  <span style={styles.resultValue}>{results.metValue}</span>
                </div>

                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t('resultIntensity')}</span>
                  <span style={styles.resultValue}>{results.intensity}</span>
                </div>

                <div style={styles.resultItem}>
                  <span style={styles.resultLabel}>{t('resultEquivalent')}</span>
                  <span style={styles.resultValue}>{results.equivalent}</span>
                </div>
              </div>

              <div style={styles.infoBox}>
                <p style={styles.infoBoxText}>
                  <strong>💡 {t('noteTitle')}</strong> {t('noteText')}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t('howToStep1')}</li>
          <li style={styles.listItem}>{t('howToStep2')}</li>
          <li style={styles.listItem}>{t('howToStep3')}</li>
          <li style={styles.listItem}>{t('howToStep4')}</li>
          <li style={styles.listItem}>{t('howToStep5')}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>

        <h3 style={styles.h3}>{t('insightMetTitle')}</h3>
        <p style={styles.paragraph}>{t('insightMetText')}</p>

        <h3 style={styles.h3}>{t('insightFormulaTitle')}</h3>
        <p style={styles.paragraph}>{t('insightFormulaText')}</p>

        <h3 style={styles.h3}>{t('insightAccuracyTitle')}</h3>
        <p style={styles.paragraph}>{t('insightAccuracyText')}</p>

        <h3 style={styles.h3}>{t('insightTdeeTitle')}</h3>
        <p style={styles.paragraph}>{t('insightTdeeText')}</p>

        <h3 style={styles.h3}>{t('insightWeightLossTitle')}</h3>
        <p style={styles.paragraph}>{t('insightWeightLossText')}</p>
      </section>

      {/* Details */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>

        <h3 style={styles.h3}>{t('detailIntensityTitle')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('detailIntensityLight')}</li>
          <li style={styles.listItem}>{t('detailIntensityModerate')}</li>
          <li style={styles.listItem}>{t('detailIntensityVigorous')}</li>
        </ul>

        <h3 style={styles.h3}>{t('detailFactorsTitle')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('detailFactor1')}</li>
          <li style={styles.listItem}>{t('detailFactor2')}</li>
          <li style={styles.listItem}>{t('detailFactor3')}</li>
          <li style={styles.listItem}>{t('detailFactor4')}</li>
          <li style={styles.listItem}>{t('detailFactor5')}</li>
        </ul>

        <h3 style={styles.h3}>{t('detailTipsTitle')}</h3>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('detailTip1')}</li>
          <li style={styles.listItem}>{t('detailTip2')}</li>
          <li style={styles.listItem}>{t('detailTip3')}</li>
          <li style={styles.listItem}>{t('detailTip4')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={styles.paragraph}>{t('faq1Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={styles.paragraph}>{t('faq2Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={styles.paragraph}>{t('faq3Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={styles.paragraph}>{t('faq4Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={styles.paragraph}>{t('faq5Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq6Question')}</h3>
          <p style={styles.paragraph}>{t('faq6Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <div style={styles.reference}>
          <a
            href="https://learn.athleanx.com/calculators/calories-burned-calculator-exercise-calculator"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
            onMouseEnter={(e) => (e.currentTarget.style.color = colors.primaryHover)}
            onMouseLeave={(e) => (e.currentTarget.style.color = colors.primary)}
          >
            {t('reference1')}
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://www.calculator.net/calories-burned-calculator.html"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
            onMouseEnter={(e) => (e.currentTarget.style.color = colors.primaryHover)}
            onMouseLeave={(e) => (e.currentTarget.style.color = colors.primary)}
          >
            {t('reference2')}
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://www.medicinenet.com/how_to_calculate_calories_burned_during_exercise/article.htm"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
            onMouseEnter={(e) => (e.currentTarget.style.color = colors.primaryHover)}
            onMouseLeave={(e) => (e.currentTarget.style.color = colors.primary)}
          >
            {t('reference3')}
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://www.medicalnewstoday.com/articles/calories-burned-in-a-day"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
            onMouseEnter={(e) => (e.currentTarget.style.color = colors.primaryHover)}
            onMouseLeave={(e) => (e.currentTarget.style.color = colors.primary)}
          >
            {t('reference4')}
          </a>
        </div>
      </section>
    </div>
  );
}